import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import { toast } from "react-toastify";

import { getMLAnalyticsSessionHistory } from "../../../actions/aiAnalytics";
import useAbortController from "../../Common/hooks/useAbortController";
import { getUser } from "../../../utils/helpers";
import AIAnalyticsFilters from "./AIAnalyticsFilters";
import AIAnalyticsPromptWorkspace from "./AIAnalyticsPromptWorkspace";
import { generateSessionId } from "./utils";

import "./AIAnalyticsPage.css";

const defaultFilters = {
  user_filter: null,
  start_date: null,
  end_date: null,
};

/** Ensures session history API data is always returned as an array. @param {*} data - raw API response data */
const normalizeSessionHistory = (data) => (Array.isArray(data) ? data : []);

/** Root AI Analytics page; wires filters, session list fetch, and the prompt workspace. */
export default function AIAnalyticsPage() {
  const currentUser = getUser();
  const hasUserFilterPermission = currentUser.permissionMap.view_ai_analytics_user_filter;
  const workspaceRef = useRef(null);
  const { setNewAbortController, abortController } = useAbortController();
  const [filters, setFilters] = useState(defaultFilters);
  const [sessions, setSessions] = useState([]);
  const [isSessionsLoading, setIsSessionsLoading] = useState(true);
  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [workspaceState, setWorkspaceState] = useState({
    isResultView: false,
    selectedPromptKey: null,
  });
  const [isUserOptionsLoading, setIsUserOptionsLoading] = useState(hasUserFilterPermission);

  const disableNewSessionButton = !workspaceState.isResultView
    && !workspaceState.selectedPromptKey;

  /** Starts a new session by generating a session id and resetting the workspace via ref. */
  const handleNewSessionOrPrompt = useCallback(() => {
    const nextSessionId = generateSessionId();
    setSessionId(nextSessionId);
    if (workspaceRef.current) {
      workspaceRef.current.startNewPrompt(nextSessionId);
    }
  }, []);

  /** Syncs workspace UI state to the page for New Prompt button enable/disable. @param {{ isResultView: boolean, selectedPromptKey: string|null }} nextState */
  const handleWorkspaceStateChange = useCallback((nextState) => {
    setWorkspaceState((prev) => {
      if (
        prev.isResultView === nextState.isResultView
        && prev.selectedPromptKey === nextState.selectedPromptKey
      ) {
        return prev;
      }
      return nextState;
    });
  }, []);

  /** Loads sidebar session history from the API. @param {Object} nextFilters - user_filter, start_date, end_date; @param {AbortSignal} signal - abort signal for the request */
  const fetchPrompts = useCallback(async (nextFilters, signal) => {
    setIsSessionsLoading(true);
    const response = await getMLAnalyticsSessionHistory({
      user_filter: nextFilters.user_filter || null,
      start_date: nextFilters.start_date || null,
      end_date: nextFilters.end_date || null,
    }, signal);
    if (response.canceled) {
      setIsSessionsLoading(false);
      return;
    }
    if (response.success) {
      setSessions(normalizeSessionHistory(response.data));
    } else {
      toast.error(response.message);
    }
    setIsSessionsLoading(false);
  }, []);

  /** Updates page-level filter state when date or user filters change. @param {Object} nextFilters - user_filter, start_date, end_date; @param {Object} [options] - options; @param {boolean} [options.force=false] - applies filters even when values are unchanged */
  const handleFiltersChange = useCallback((nextFilters, { force = false } = {}) => {
    setFilters((prev) => {
      if (
        !force
        && prev.user_filter === nextFilters.user_filter
        && prev.start_date === nextFilters.start_date
        && prev.end_date === nextFilters.end_date
      ) {
        return prev;
      }
      return nextFilters;
    });
  }, []);

  /** Re-fetches the sidebar prompt list using the current page filters. @returns {Promise<void>} */
  const refetchPrompts = useCallback(() => fetchPrompts(filters), [fetchPrompts, filters]);

  useEffect(() => {
    if (hasUserFilterPermission && isUserOptionsLoading) return undefined;

    if (abortController.current) {
      abortController.current.abort();
    }
    const controller = setNewAbortController();
    fetchPrompts(filters, controller.signal);
    return () => {
      controller.abort();
    };
  }, [filters, fetchPrompts, hasUserFilterPermission]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="AIAnalytics__Container">
      <AIAnalyticsFilters
        disableNewSessionButton={disableNewSessionButton}
        onNewPrompt={handleNewSessionOrPrompt}
        onFiltersChange={handleFiltersChange}
        isUserOptionsLoading={isUserOptionsLoading}
        setIsUserOptionsLoading={setIsUserOptionsLoading}
      />
      <AIAnalyticsPromptWorkspace
        ref={workspaceRef}
        sessions={sessions}
        isSessionsLoading={isSessionsLoading}
        sessionId={sessionId}
        onWorkspaceStateChange={handleWorkspaceStateChange}
        onPromptsRefetch={refetchPrompts}
      />
    </div>
  );
}
